import { AdyenCheckout, ApplePay, Card } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import { runWithAdyenSessionRetry } from '@js/adyen-session-retry';
import { applyCsrfHeaders } from '@js/csrf';

// Global object for other scripts to read pre-auth card data once all required fields are valid
window.preAuth = {};

const getHeaders = () => {
	const headers = new Headers();
	headers.append('current-page', (window.location && window.location.pathname) || null);
	headers.append('Accept', 'application/json');
	headers.append('Content-Type', 'application/json;charset=UTF-8');
	headers.append('source', 'vshred');
	applyCsrfHeaders(headers);

	return headers;
};

const extractAdyenAdditionalDetails = (state) => {
	const root = state && state.data !== undefined ? state.data : state;
	const detailsFromRoot = root && typeof root === 'object' && 'details' in root
		? root.details
		: root;

	return detailsFromRoot !== undefined
		? detailsFromRoot
		: (state && state.details !== undefined ? state.details : {});
};

window.AdyenThreeDs = {
	payContext: null,

	async submitDetails(details) {
		const response = await fetch('/api/cart/pay/adyen-details', {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify({ details: details || {} }),
		});

		if (!response.ok) {
			let message = 'We could not verify your card. Please try again.';

			if (response.status === 419) {
				message = 'Your session has expired. Please refresh the page and try again.';
			} else if (response.status === 429) {
				message = 'Too many attempts. Please wait a moment and try again.';
			} else {
				try {
					const json = await response.json();
					if (typeof json?.message === 'string' && json.message !== '') {
						message = json.message;
					} else if (Array.isArray(json?.errors?.details) && json.errors.details[0]) {
						message = json.errors.details[0];
					} else if (typeof json?.errors?.message === 'string' && json.errors.message !== '') {
						message = json.errors.message;
					}
				} catch (parseError) {
					console.error('[Adyen|3DS] submitDetails response parse error:', parseError);
				}
			}

			throw new Error(message);
		}

		return response.json();
	},

	async continueAfterDetails(details) {
		try {
			const json = await this.submitDetails(details);

			if (json.type && json.type === 'error') {
				this.payContext?.onError?.(json.message || 'We could not verify your card. Please try again.');
				return;
			}

			const meta = json.meta;

			if (meta?.payment?.requires_action === true && meta.payment?.action) {
				await window.cardComponent.handleAction(meta.payment.action);
				return;
			}

			if (meta?.next_url) {
				this.payContext?.onComplete?.(meta.next_url);
				return;
			}

			this.payContext?.onError?.('There was an issue completing your payment.');
		} catch (error) {
			console.error('[Adyen|3DS] continueAfterDetails:', error);
			this.payContext?.onError?.(error?.message || 'We could not verify your card. Please try again.');
		}
	},

	async handlePayResponse(meta, context) {
		if (!(meta?.payment?.requires_action === true && meta.payment?.action)) {
			return false;
		}

		if (!window.cardComponent || typeof window.cardComponent.handleAction !== 'function') {
			context?.onError?.('Card form is no longer available. Please refresh the page and try again.');
			return true;
		}

		this.payContext = context;

		try {
			await window.cardComponent.handleAction(meta.payment.action);
			return true;
		} catch (error) {
			console.error('[Adyen|3DS] handlePayResponse:', error);
			context?.onError?.(error?.message || 'We could not start card verification. Please try again.');
			return true;
		}
	},
};

const loadPaymentMethods = async() => fetch('/api/adyen/payment/methods', {
	method: 'GET',
	headers: getHeaders()
})
	.then(response => response.json())
	.then(data => data)
	.catch(error => console.error('Error fetching payment methods:', error));

const initializeCardStorage = async () => {
	const paymentMethods = await loadPaymentMethods();
	try {
		const checkout = await runWithAdyenSessionRetry(
			async () => {
				const response = await fetch('/api/adyen/payment/session', {
					method: 'GET',
					headers: getHeaders()
				});
				const data = await response.json();

				const configuration = {
					locale: 'en_US',
					countryCode: 'US',
					environment: data.environment,
					clientKey: data.clientKey,
					showPayButton: false,
					session: data.session,
					onError: (error) => console.error('[Adyen] Error:', error),
					paymentMethodsResponse: paymentMethods
				};

				return AdyenCheckout(configuration);
			},
			{
				onRetry: () => {
					console.warn('[Adyen] Session expired during checkout initialization. Retrying with a new session.');
				}
			}
		);

		const preAuthFields = {
			endDigits: null,
			issuerBin: null,
			expiryDate: null,
			cardBrand: null,
		};

		const buildPreAuth = (cardholderName = '') => {
			const [expirationMonth = '', expirationYear = ''] = (preAuthFields.expiryDate || '').split('/');
			return {
				cardholderName,
				issuerBin: String(preAuthFields.issuerBin),
				endDigits: preAuthFields.endDigits,
				expiryDate: preAuthFields.expiryDate,
				expirationMonth,
				expirationYear,
				cardBrand: preAuthFields.cardBrand,
			};
		};

		const setPreAuthIfComplete = (cardholderName = '') => {
			if (Object.values(preAuthFields).every(value => value !== null)) {
				window.preAuth = buildPreAuth(cardholderName);
			}
		};

		const cardConfiguration = {
			enableStoreDetails: false,
			billingAddressRequired: false,
			name: 'Credit Card',
			showStoreDetails: false,
			storeDetailsInitialState: false,
			exposeExpiryDate: true,
			onBinLookup: (response) => {
				if (response.detectedBrands && response.detectedBrands.length > 0) {
					preAuthFields.cardBrand = response.detectedBrands[0];
				}
			},
			onFieldValid: (response) => {
				if (response.valid) {
					const preAuthKeys = Object.keys(preAuthFields);
					preAuthKeys.forEach(key => {
						if (Object.prototype.hasOwnProperty.call(response, key)) {
							preAuthFields[key] = response[key];
						}
					});
					setPreAuthIfComplete();
				}
			},
			onChange: (state) => {
				if (state.isValid) {
					const cardholderName = state.data?.holderName ?? '';
					setPreAuthIfComplete(cardholderName);
				}
				window.cardState = state;
			},
			onAdditionalDetails: async (state) => {
				await window.AdyenThreeDs.continueAfterDetails(extractAdyenAdditionalDetails(state));
			},
			onSubmit: (state, component, actions) => {
				actions.reject();
			},
		};
		window.cardComponent = new Card(checkout, cardConfiguration).mount('#adyen-checkout-container');

		// Apple Pay component initialization with tokenization support
		const applePayMethod = paymentMethods?.paymentMethods?.find(pm => pm.type === 'applepay');
		if (applePayMethod) {
			const applePayConfiguration = {
				amount: {
					value: 0, // Will be updated when cart amount is known
					currency: 'USD'
				},
				onSubmit: async (state, component) => {
					// This will be handled by the Adyen component in js-v2.blade.php
					// Store state globally for access
					window.applePayState = state;
					window.applePayComponent = component;

					// Trigger custom event for the checkout component to handle
					window.dispatchEvent(new CustomEvent('apple-pay-submit', {
						detail: { state, component }
					}));
				},
				onError: (error, component) => {
					console.error('[Adyen] Apple Pay error:', error);
					component.setStatus('error');

					// Trigger error event
					window.dispatchEvent(new CustomEvent('apple-pay-error', {
						detail: { error, component }
					}));
				}
			};

			window.applePayComponent = new ApplePay(checkout, applePayConfiguration);
			// Mark Apple Pay as available
			window.applePayAvailable = true;
		} else {
			window.applePayAvailable = false;
		}
	} catch (error) {
		console.error('Error initializing card storage:', error);
	}
};

const isAdyenGateway = () => {
	// Try to get the gateway string to use
	let gatewayMeta;
	try {
		gatewayMeta = window.atob(document.head.querySelector('meta[name="gw"]').content);
	} catch (error) {
		console.error('No billing gateway found!', error);
	}
	return gatewayMeta === 'Adyen' || gatewayMeta === 'Adyen_Testing';
};

document.addEventListener('DOMContentLoaded', async () => {
	if (isAdyenGateway()) {
		await initializeCardStorage();
	}
});
